import { Api } from '../services/api.ts';
import type { Capitulo } from '../types/index.ts';
import { compressImageToBase64 } from '../utils/imageCompressor.ts';

let capitulosCache: Capitulo[] = [];
let saveTimeout: number | undefined;
let currentChapterId: string = '';

export function initEditor(onStatsUpdate: (totalWords: number) => void) {
  const chapterSelect = document.getElementById('chapter-select') as HTMLSelectElement | null;
  const chapterEditor = document.getElementById('chapter-content') as HTMLDivElement | null;
  const statWords = document.getElementById('stat-words') as HTMLElement | null;
  const saveStatus = document.getElementById('save-status') as HTMLElement | null;
  const inlineImageInput = document.getElementById('inline-image-input') as HTMLInputElement | null;
  const btnExportWord = document.getElementById('btn-export-word') as HTMLButtonElement | null;
  const btnExportPdf = document.getElementById('btn-export-pdf') as HTMLButtonElement | null;
  const btnSaveChapter = document.getElementById('btn-save-chapter') as HTMLButtonElement | null;
  const btnAddChapter = document.getElementById('btn-add-chapter') as HTMLButtonElement | null;
  const btnRenameChapter = document.getElementById('btn-rename-chapter') as HTMLButtonElement | null;
  const btnDelChapter = document.getElementById('btn-del-chapter') as HTMLButtonElement | null;

  if (!chapterSelect || !chapterEditor) return;

  const selectEl = chapterSelect;
  const editorEl = chapterEditor;

  function setSaveStatus(type: 'saved' | 'saving' | 'error', text: string) {
    if (!saveStatus) return;
    saveStatus.className = `sync-status sync-${type}`;
    saveStatus.innerHTML = `<span class="status-indicator-dot"></span><span class="sync-status-text">${text}</span>`;
  }

  function updateWordCounts() {
    const text = editorEl.innerText.trim();
    const currentWords = text ? text.split(/\s+/).length : 0;
    if (statWords) statWords.innerText = currentWords.toLocaleString();

    let totalWords = 0;
    const curKey = selectEl.value;
    capitulosCache.forEach(c => {
      if (c.id.toString() === curKey) {
        totalWords += currentWords;
      } else {
        const div = document.createElement('div');
        div.innerHTML = c.contenido_html || '';
        const t = div.innerText.trim();
        totalWords += t ? t.split(/\s+/).length : 0;
      }
    });

    onStatsUpdate(totalWords);
  }

  function renderCurrentChapter() {
    const curKey = selectEl.value;
    currentChapterId = curKey;
    const curCap = capitulosCache.find(c => c.id.toString() === curKey);
    editorEl.innerHTML = curCap?.contenido_html || '<h2>Comienza a redactar este capítulo...</h2><p>Pega aquí tu texto desde Word o inserta planos y fotografías.</p>';
    updateWordCounts();
  }

  function getEffectiveChapterId(preferredId?: string | number): string {
    if (preferredId !== undefined && preferredId !== '') {
      return preferredId.toString();
    }
    // Revisar si la URL tiene parámetro de capítulo (ej. #redaccion?cap=3 o #redaccion/cap-3 o #redaccion/3)
    const hash = window.location.hash;
    const match = hash.match(/(?:[?&]cap=|\/cap-|\/|capitulo[-=])(\d+)/i);
    if (match && match[1]) {
      return match[1];
    }
    const saved = localStorage.getItem('doctoral_active_chapter_id');
    if (saved) return saved;
    return '';
  }

  async function loadChapters(preferredId?: string | number) {
    try {
      const requestedId = getEffectiveChapterId(preferredId);
      capitulosCache = await Api.getCapitulos();
      selectEl.innerHTML = '';
      
      capitulosCache.forEach(c => {
        const opt = document.createElement('option');
        opt.value = c.id.toString();
        opt.textContent = c.titulo || c.name || `Capítulo ${c.numero}`;
        selectEl.appendChild(opt);
      });

      if (capitulosCache.length > 0) {
        // Intentar buscar por ID o por número de capítulo
        const matched = capitulosCache.find(c => c.id.toString() === requestedId || c.numero.toString() === requestedId);
        if (matched) {
          selectEl.value = matched.id.toString();
        } else {
          selectEl.value = capitulosCache[0].id.toString();
        }
      }

      localStorage.setItem('doctoral_active_chapter_id', selectEl.value);
      renderCurrentChapter();
    } catch (err) {
      console.error('Error cargando capítulos desde SQLite:', err);
      setSaveStatus('error', 'Error al cargar capítulos desde SQLite');
    }
  }

  async function saveChapter(isManual = false) {
    const curKey = selectEl.value;
    if (!curKey) return;
    const curCap = capitulosCache.find(c => c.id.toString() === curKey);
    const html = editorEl.innerHTML;
    if (curCap) {
      curCap.contenido_html = html;
    }

    setSaveStatus('saving', isManual ? 'Guardando en SQLite...' : 'Auto-guardando en SQLite...');
    try {
      const res = await Api.updateCapitulo(curKey, html, curCap?.name || curCap?.titulo);
      if (curCap && res.palabras_totales !== undefined) {
        curCap.palabras_totales = res.palabras_totales;
      }
      setSaveStatus('saved', isManual ? '¡Capítulo guardado con éxito en SQLite!' : 'Guardado en SQLite relacional');
      updateWordCounts();
    } catch (err) {
      console.error('Error guardando capítulo en SQLite:', err);
      setSaveStatus('error', 'Error al guardar en SQLite');
    }
  }

  // Cambio de capítulo en el desplegable
  selectEl.addEventListener('change', async () => {
    if (saveTimeout) {
      window.clearTimeout(saveTimeout);
      if (currentChapterId) {
        const prevCap = capitulosCache.find(c => c.id.toString() === currentChapterId);
        if (prevCap) {
          try {
            await Api.updateCapitulo(currentChapterId, prevCap.contenido_html, prevCap.name);
          } catch (e) {
            console.error('Error al persistir capítulo anterior:', e);
          }
        }
      }
    }
    localStorage.setItem('doctoral_active_chapter_id', selectEl.value);
    if (window.location.hash.startsWith('#redaccion') || !window.location.hash) {
      window.history.replaceState(null, '', `#redaccion/cap-${selectEl.value}`);
    }
    renderCurrentChapter();
  });

  // Guardado automático por debouncing al escribir
  editorEl.addEventListener('input', () => {
    const curKey = selectEl.value;
    const curCap = capitulosCache.find(c => c.id.toString() === curKey);
    if (curCap) {
      curCap.contenido_html = editorEl.innerHTML;
    }

    setSaveStatus('saving', 'Escribiendo... guardado en SQLite pendiente');
    window.clearTimeout(saveTimeout);

    saveTimeout = window.setTimeout(() => {
      saveChapter(false);
    }, 700);
  });

  // Botón manual: Guardar Capítulo
  btnSaveChapter?.addEventListener('click', (e) => {
    e.preventDefault();
    window.clearTimeout(saveTimeout);
    saveChapter(true);
  });

  // Botón: Añadir Nuevo Capítulo (+ Nuevo Cap.)
  btnAddChapter?.addEventListener('click', async (e) => {
    e.preventDefault();
    const nextNum = capitulosCache.length + 1;
    const defaultTitle = `Capítulo ${nextNum}: Nuevo Capítulo de Investigación`;
    const title = window.prompt('Ingrese el título o nombre del nuevo capítulo para la tesis:', defaultTitle);
    if (!title || !title.trim()) return;

    setSaveStatus('saving', 'Creando capítulo en SQLite...');
    try {
      const res = await Api.addCapitulo({
        name: title.trim(),
        titulo: title.trim(),
        numero: nextNum,
        contenido_html: `<h2>${title.trim()}</h2><p>Comienza a redactar el contenido de este capítulo...</p>`,
        estado_edicion: 'borrador'
      });
      await loadChapters(res.id);
      setSaveStatus('saved', `Capítulo "${title.trim()}" creado en SQLite`);
    } catch (err: any) {
      console.error('Error al crear capítulo:', err);
      setSaveStatus('error', 'Error al crear capítulo');
      alert('Error al crear capítulo en SQLite: ' + (err?.message || err));
    }
  });

  // Botón: Renombrar Título del Capítulo
  btnRenameChapter?.addEventListener('click', async (e) => {
    e.preventDefault();
    const curKey = selectEl.value;
    if (!curKey) return;
    const curCap = capitulosCache.find(c => c.id.toString() === curKey);
    if (!curCap) return;

    const currentTitle = curCap.titulo || curCap.name;
    const newTitle = window.prompt('Editar título del capítulo actual:', currentTitle);
    if (!newTitle || !newTitle.trim() || newTitle.trim() === currentTitle) return;

    const cleanTitle = newTitle.trim();
    curCap.name = cleanTitle;
    curCap.titulo = cleanTitle;

    setSaveStatus('saving', 'Actualizando título en SQLite...');
    try {
      await Api.updateCapitulo(curKey, editorEl.innerHTML, cleanTitle);
      const opt = selectEl.querySelector(`option[value="${curKey}"]`);
      if (opt) opt.textContent = cleanTitle;
      setSaveStatus('saved', 'Título actualizado en SQLite');
    } catch (err: any) {
      console.error('Error al renombrar capítulo:', err);
      setSaveStatus('error', 'Error al renombrar capítulo');
      alert('Error al renombrar capítulo: ' + (err?.message || err));
    }
  });

  // Botón: Eliminar Capítulo
  btnDelChapter?.addEventListener('click', async (e) => {
    e.preventDefault();
    const curKey = selectEl.value;
    if (!curKey) return;
    const curCap = capitulosCache.find(c => c.id.toString() === curKey);
    if (!curCap) return;

    if (capitulosCache.length <= 1) {
      alert('La tesis doctoral debe conservar al menos un capítulo en la base de datos.');
      return;
    }

    const title = curCap.titulo || curCap.name;
    const ok = window.confirm(`¿Está segura de que desea eliminar permanentemente este capítulo de SQLite?\n\n"${title}"\n\nEsta acción eliminará el contenido y los textos asociados.`);
    if (!ok) return;

    setSaveStatus('saving', 'Eliminando capítulo de SQLite...');
    try {
      await Api.deleteCapitulo(curKey);
      await loadChapters();
      setSaveStatus('saved', 'Capítulo eliminado de SQLite');
    } catch (err: any) {
      console.error('Error al eliminar capítulo:', err);
      setSaveStatus('error', 'Error al eliminar capítulo');
      alert('Error al eliminar capítulo en SQLite: ' + (err?.message || err));
    }
  });

  // Atajo de teclado: Ctrl + S o Cmd + S
  window.addEventListener('keydown', (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
      const activeSection = document.getElementById('mod-redaccion');
      if (activeSection?.classList.contains('active')) {
        e.preventDefault();
        window.clearTimeout(saveTimeout);
        saveChapter(true);
      }
    }
  });

  // Inserción de imágenes con compresión automática a Base64 WebP/JPEG optimizado
  if (inlineImageInput) {
    inlineImageInput.addEventListener('change', async (e) => {
      const target = e.target as HTMLInputElement;
      const file = target.files?.[0];
      if (!file) return;

      setSaveStatus('saving', 'Comprimiendo imagen en Base64 optimizado...');
      try {
        const compressed = await compressImageToBase64(file, 1600, 1600, 0.82);
        document.execCommand('insertImage', false, compressed.dataUrl);
        
        const curKey = selectEl.value;
        const html = editorEl.innerHTML;
        const curCap = capitulosCache.find(c => c.id.toString() === curKey);
        if (curCap) curCap.contenido_html = html;

        await Api.updateCapitulo(curKey, html, curCap?.name || curCap?.titulo);
        const compKb = (compressed.compressedSize / 1024).toFixed(0);
        setSaveStatus('saved', `Imagen insertada (${compKb} KB, -${compressed.ratioPercent}%) y guardada en SQLite`);
        updateWordCounts();
      } catch (err) {
        setSaveStatus('error', 'Error al procesar o guardar imagen');
        console.error('Error insertando imagen comprimida en SQLite:', err);
      } finally {
        target.value = '';
      }
    });
  }

  // Exportar Word (.doc)
  if (btnExportWord) {
    btnExportWord.addEventListener('click', () => {
      const curKey = selectEl.value;
      const cap = capitulosCache.find(c => c.id.toString() === curKey);
      const content = cap?.contenido_html || editorEl.innerHTML;

      const header = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
      <head><meta charset='utf-8'><title>Tesis Doctoral - ${cap?.titulo || 'Capitulo'}</title>
      <style>
        body { font-family: 'Georgia', serif; font-size: 12pt; line-height: 1.6; color: #111827; }
        h1 { font-family: Arial, sans-serif; color: #1e3a8a; font-size: 18pt; margin-bottom: 12pt; }
        h2 { font-family: Arial, sans-serif; color: #1f2937; font-size: 14pt; margin-top: 14pt; margin-bottom: 6pt; }
        p { margin-bottom: 10pt; text-align: justify; }
        blockquote { font-style: italic; margin-left: 20pt; border-left: 3pt solid #2563eb; padding-left: 10pt; }
        img { max-width: 100%; height: auto; margin: 10pt 0; }
      </style></head><body>`;
      const footer = "</body></html>";
      const sourceHTML = header + content + footer;

      const blob = new Blob(['\ufeff' + sourceHTML], { type: 'application/msword;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Tesis_Damke_${cap?.name ? cap.name.replace(/[^a-zA-Z0-9_-]/g, '_') : curKey}_${new Date().toISOString().slice(0, 10)}.doc`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    });
  }

  // Exportar PDF
  if (btnExportPdf) {
    btnExportPdf.addEventListener('click', () => {
      window.print();
    });
  }

  // Carga inicial
  loadChapters();
}

export function formatDoc(cmd: string, value: string | undefined = undefined) {
  document.execCommand(cmd, false, value);
  const chapterEditor = document.getElementById('chapter-content');
  chapterEditor?.focus();
}

export function selectChapterById(id: string | number) {
  const chapterSelect = document.getElementById('chapter-select') as HTMLSelectElement | null;
  if (!chapterSelect) return;
  const targetId = id.toString();
  const matched = capitulosCache.find(c => c.id.toString() === targetId || c.numero.toString() === targetId);
  if (matched && chapterSelect.value !== matched.id.toString()) {
    chapterSelect.value = matched.id.toString();
    chapterSelect.dispatchEvent(new Event('change'));
  }
}
